/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * SettingsView — admin-only Settings page.
 *
 * Two sections:
 *   1. Tenant brand accent (admin-only). Pick one of 8 swatches; on Save,
 *      PATCHes /tenant/settings and refreshes the AuthContext user so the UI
 *      reflects the new brand. Save is required (no live preview) so other
 *      tenant users don't see flicker mid-edit.
 *   2. Your theme preference (any user). 3 radio buttons; calls
 *      useTheme().setPreference() which already PATCHes
 *      /auth/me/preferences (Agent M's extension) and applies to <html>.
 *
 * The ThemeToggle in the AppShell header still works — Settings is a
 * more discoverable surface that does the same thing. They stay in sync
 * via shared ThemeContext state.
 */
import React, { useState } from 'react';
import { ViewHeader } from '@/components/ViewHeader';
import { BrandAccentPicker } from '@/components/BrandAccentPicker';
import { useAuth } from '@/context/AuthContext';
import { useTheme, type ThemePref } from '@/context/ThemeContext';
import {
  patchTenantSettings,
  patchUserPreferences,
} from '@/api/settings';
import type { BrandAccent } from '@/data/brandSwatches';

type SaveStatus =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'success'; message: string }
  | { kind: 'error'; message: string };

const THEME_OPTIONS: Array<{ value: ThemePref; label: string; hint: string }> = [
  { value: 'light', label: 'Light', hint: 'Always light' },
  { value: 'dark', label: 'Dark', hint: 'Always dark' },
  { value: 'system', label: 'System', hint: 'Follow OS preference' },
];

function isAdmin(role?: string | null): boolean {
  if (!role) return false;
  return role.toUpperCase() === 'ADMIN';
}

export const SettingsView: React.FC = () => {
  const auth = useAuth() as any;
  const { user } = auth;
  const { preference, setPreference } = useTheme();

  const initialAccent: BrandAccent =
    (user?.tenant_brand_accent as BrandAccent | undefined) ?? 'red';

  const [brandAccent, setBrandAccent] = useState<BrandAccent>(initialAccent);
  const [brandStatus, setBrandStatus] = useState<SaveStatus>({ kind: 'idle' });
  const [themeStatus, setThemeStatus] = useState<SaveStatus>({ kind: 'idle' });

  const admin = isAdmin(user?.role);
  const dirty = brandAccent !== initialAccent;

  const handleBrandChange = (next: BrandAccent) => {
    setBrandAccent(next);
    // Intentionally do NOT applyBrandAccent() here — change is local until
    // the user clicks "Apply tenant-wide" so other tenant users don't see
    // mid-edit flicker.
    if (brandStatus.kind === 'success' || brandStatus.kind === 'error') {
      setBrandStatus({ kind: 'idle' });
    }
  };

  const handleSaveBrand = async () => {
    if (!dirty) return;
    const confirmed = window.confirm(
      'This will change the brand color for everyone in your campaign. Continue?'
    );
    if (!confirmed) return;

    setBrandStatus({ kind: 'saving' });
    try {
      await patchTenantSettings({ brand_accent: brandAccent });
      // Refresh AuthContext user so the rest of the UI picks up the new
      // brand. AuthContext exposes setUser in some builds; if not, fall
      // back to a soft refetch.
      if (typeof auth.setUser === 'function' && user) {
        auth.setUser({ ...user, tenant_brand_accent: brandAccent });
      } else if (typeof auth.refetchUser === 'function') {
        await auth.refetchUser();
      }
      setBrandStatus({
        kind: 'success',
        message: 'Brand accent applied tenant-wide.',
      });
    } catch (err: any) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to update tenant brand. Please try again.';
      setBrandStatus({ kind: 'error', message });
    }
  };

  const handleThemeChange = async (next: ThemePref) => {
    // Apply locally + persist via ThemeContext (already updates <html>
    // and localStorage). Agent M's extended setPreference also fires the
    // PATCH /auth/me/preferences call.
    setPreference(next);
    setThemeStatus({ kind: 'saving' });
    try {
      await patchUserPreferences({ theme_preference: next });
      setThemeStatus({ kind: 'success', message: 'Saved' });
      // Auto-clear after a short pause.
      window.setTimeout(() => {
        setThemeStatus((s) => (s.kind === 'success' ? { kind: 'idle' } : s));
      }, 2000);
    } catch (err: any) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        'Saved locally, but the server did not confirm.';
      setThemeStatus({ kind: 'error', message });
    }
  };

  const saving = brandStatus.kind === 'saving';

  return (
    <div className="space-y-6">
      <ViewHeader
        title="Settings"
        tagline="Tenant brand and your theme preference."
      />

      {/* ---------------- Tenant brand accent ---------------- */}
      <section className="bg-bg-raised border border-border-base rounded-lg p-6 space-y-4">
        <div>
          <h2 className="text-lg font-bold text-fg-primary">
            Tenant brand accent
          </h2>
          <p className="text-fg-secondary text-sm mt-1">
            Pick the accent color used across buttons, links, and highlights
            for everyone in your campaign. This change is shared by all users
            in your tenant.
          </p>
        </div>

        {!admin && (
          <div
            role="status"
            className="bg-status-warning-bg/20 text-status-warning border border-status-warning rounded p-3 text-sm"
          >
            Settings are admin-only. Ask a campaign admin to change the brand
            accent.
          </div>
        )}

        {admin && (
          <>
            <BrandAccentPicker
              value={brandAccent}
              onChange={handleBrandChange}
              disabled={saving}
            />

            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={handleSaveBrand}
                disabled={!dirty || saving}
                className="bg-accent hover:bg-accent-hover text-white px-4 py-2 rounded-md font-bold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Applying…' : 'Apply tenant-wide'}
              </button>
              {!dirty && brandStatus.kind === 'idle' && (
                <span className="text-fg-muted text-sm">
                  No changes to save.
                </span>
              )}
            </div>

            {brandStatus.kind === 'success' && (
              <div
                role="status"
                className="bg-status-success-bg/20 text-status-success border border-status-success rounded p-3 text-sm"
              >
                {brandStatus.message}
              </div>
            )}
            {brandStatus.kind === 'error' && (
              <div
                role="alert"
                className="bg-status-danger-bg/20 text-status-danger border border-status-danger rounded p-3 text-sm"
              >
                {brandStatus.message}
              </div>
            )}
          </>
        )}
      </section>

      {/* ---------------- Theme preference ---------------- */}
      <section className="bg-bg-raised border border-border-base rounded-lg p-6 space-y-4">
        <div>
          <h2 className="text-lg font-bold text-fg-primary">
            Your theme preference
          </h2>
          <p className="text-fg-secondary text-sm mt-1">
            Choose a light or dark theme, or follow your operating system. This
            only affects you on this device + account.
          </p>
        </div>

        <fieldset className="space-y-2">
          <legend className="sr-only">Theme preference</legend>
          {THEME_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className="flex items-start gap-3 p-2 rounded-md hover:bg-bg-elevated cursor-pointer min-h-11"
            >
              <input
                type="radio"
                name="theme-preference"
                value={opt.value}
                checked={preference === opt.value}
                onChange={() => handleThemeChange(opt.value)}
                className="mt-1 h-4 w-4 accent-accent"
              />
              <span className="flex flex-col">
                <span className="text-fg-primary font-medium">{opt.label}</span>
                <span className="text-fg-muted text-xs">{opt.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {themeStatus.kind === 'success' && (
          <div
            role="status"
            aria-live="polite"
            className="bg-status-success-bg/20 text-status-success border border-status-success rounded p-3 text-sm inline-flex items-center gap-2"
          >
            <span aria-hidden="true">✓</span>
            <span>{themeStatus.message}</span>
          </div>
        )}
        {themeStatus.kind === 'error' && (
          <div
            role="alert"
            className="bg-status-danger-bg/20 text-status-danger border border-status-danger rounded p-3 text-sm"
          >
            {themeStatus.message}
          </div>
        )}
      </section>
    </div>
  );
};

export default SettingsView;
